import { useCallback, useEffect, useRef, useState } from "react";

export type ResourceState<T> =
  | { status: "loading"; data?: undefined; error?: undefined }
  | { status: "success"; data: T; error?: undefined; refreshing?: boolean }
  | { status: "error"; data?: T | undefined; error: Error };

export function useResource<T>(loader: () => Promise<T>, dependencies: readonly unknown[] = []) {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<ResourceState<T>>({ status: "loading" });
  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setState((previous) => previous.status === "success"
      ? { status: "success", data: previous.data, refreshing: true }
      : { status: "loading" });
    void loaderRef.current().then(
      (data) => { if (!controller.signal.aborted) setState({ status: "success", data }); },
      (error: unknown) => { if (!controller.signal.aborted) setState((previous) => ({ status: "error", data: previous.data, error: error instanceof Error ? error : new Error("Request failed") })); },
    );
    return () => controller.abort();
    // Dependency values are deliberately supplied by callers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, ...dependencies]);

  return { state, refresh };
}
